import { ThemeProvider } from '@mui/material/styles';
import { darkTheme, ThemeProvider as DSThemeProvider, Snackbar } from '@shape-ds/components';
import {
  ADD_AUTH_TOKEN,
  ADD_MESSAGE,
  AppContext,
  appReducer,
  appState,
  appStateFake,
  CLEAR_MESSAGE,
  REMOVE_AUTH_TOKEN,
} from 'appContext';
import { ErrorFallback } from 'components/ErrorFallback/ErrorFallback';
import { IS_DEMO, IS_PROD, LOCAL_STORAGE_WORKSPACE_ID, POSTHOG_URL } from 'config/constants';
import { FeatureFlagProvider } from '@shape/feature-flag';
import { I18nProvider } from 'i18n';
import { PostHogConfig } from 'posthog-js';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useReducer } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { AppRoutes } from 'routes/AppRoutes';
import * as types from 'types';
import { shapeTheme } from 'ui/Select/SelectTheme';
import './App.scss';
import { AuthProvider } from '@shape/auth-context';

const options: Partial<PostHogConfig> = {
  api_host: POSTHOG_URL,
  persistence: 'localStorage',
  autocapture: IS_PROD,
  capture_pageview: IS_PROD,
  capture_pageleave: IS_PROD,
  capture_performance: IS_PROD,
};

function App() {
  const [state, dispatch] = useReducer(appReducer, IS_DEMO ? appStateFake : appState);
  const posthog = usePostHog();
  const contextValue = useMemo(() => ({ state, dispatch }), [state, dispatch]);

  useEffect(() => {
    window.addEventListener(ADD_AUTH_TOKEN, ((event: CustomEvent) => {
      dispatch({ type: ADD_AUTH_TOKEN, token: event.detail });
    }) as EventListener);

    window.addEventListener(REMOVE_AUTH_TOKEN, () => {
      dispatch({ type: REMOVE_AUTH_TOKEN });
    });
    const url = new URL(window.location.href);
    if (url.searchParams.has('edit')) {
      url.searchParams.delete('edit');
      window.history.replaceState(null, '', url.toString());
    }

    window.addEventListener(ADD_MESSAGE, ((
      event: CustomEvent<{
        message: string;
        title: string;
        type: 'error' | 'warning' | 'info' | 'success';
        id: string;
      }>,
    ) => {
      dispatch({
        type: ADD_MESSAGE,
        text: event.detail.message,
        messageType: event.detail.type,

        id: event.detail.id,
      });
    }) as EventListener);
  }, []);
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    window.location.reload();
  });

  const handleCloseSnackbar = (_id: string) => {
    dispatch({ type: CLEAR_MESSAGE });
  };
  useEffect(() => {
    if (state.auth.user && posthog) {
      const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID);
      posthog.identify(state.auth.user.email || state.auth.user.emails?.[0], {
        'workspace-id': workspaceId,
        email: state.auth.user.email || state.auth.user.emails?.[0],
        env: import.meta.env.VITE_ENVIRONMENT,
        app_version: __APP_VERSION__,
      });
    }
  }, [posthog, state.auth.user]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AppContext.Provider value={contextValue}>
        <AuthProvider>
          <I18nProvider>
            <PostHogProvider apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY} options={options}>
              <ThemeProvider theme={shapeTheme}>
                <DSThemeProvider theme={darkTheme}>
                  <FeatureFlagProvider>
                    <div>
                      {state.messages.map((item: types.MessageInterface) => {
                        return (
                          <Snackbar
                            key={`alert-shell-${item.id}`}
                            open={state.messages.length > 0}
                            message={item.text}
                            severity={item.messageType}
                            handleClose={() => handleCloseSnackbar(item.id)}
                            autoHideDuration={5000}
                          />
                        );
                      })}
                    </div>
                    <AppRoutes />
                  </FeatureFlagProvider>
                </DSThemeProvider>
              </ThemeProvider>
            </PostHogProvider>
          </I18nProvider>
        </AuthProvider>
      </AppContext.Provider>
    </ErrorBoundary>
  );
}

export default App;
